import React from 'react';
import styled, { css } from 'styled-components';
import { Tabs } from 'react-tabs';

import media from '../../../utils/styling/media';

export const StyledTabs = styled(({
  small, large, paddingXs, paddingSm, tabListItemHorizontalPaddingXs, tabListItemHorizontalPaddingSm, $background, $selectedColor, $selectedTabBorderSize, $selectedTabBorder, $unselectedTabBackground, $unselectedTabColor, $hoverBackground, $flex, $fontWeight, $tabBorder, ...props
}) => <Tabs {...props} />)`
  .react-tabs {
    -webkit-tap-highlight-color: transparent;

    &__tab-list {
      margin: 0;
      padding: 0;
      display: flex;
      align-items: flex-end;
    }

    &.ia-react-core_tabs--standard .react-tabs__tab-list {
      border-bottom: 0;
    }

    &.ia-react-core_tabs--light .react-tabs__tab-list {
      border-bottom: ${({ theme }) => `1px solid ${theme.colors.gray4}`};
    }

    &__tab {
      border-bottom: none;
      bottom: -1px;
      position: relative;
      list-style: none;
      padding: 18px 16px 0 16px;
      cursor: pointer;
      box-sizing: border-box;
      height: 54px;
      background: ${({ theme, $unselectedTabBackground }) => $unselectedTabBackground || theme.colors.shadowGray2};
      color: ${({ theme, $unselectedTabColor }) => $unselectedTabColor || theme.colors.white};
      font-weight: ${({ $fontWeight }) => $fontWeight};
      font-size: 17px;
      margin-right: ${({ $tabMargin }) => $tabMargin || '8px'};
      white-space: nowrap;
      outline: 0;
      flex: ${({ $flex }) => $flex || '1'};
      text-align: center;

      ${media.sm`
        flex: 0;
        text-align: inherit;
      `}

      &:last-child {
        margin-left: 0;
        margin-right: 0;
      }

      ${media.lg`
        padding-left: 32px;
        padding-right: 32px;

        &.ia-react-core_tabs--compact {
          padding-right: 16px;
          padding-left: 16px;
        }
      `}

      &.ia-react-core_tabs--compact {
        height: 30px;
        padding-top: 7px;
        font-size: 13px;
        padding-right: 16px;
        padding-left: 16px;
      }

      &.ia-react-core_tabs--small {
        height: 50px;
        padding-top: 17px;
        font-size: 14px;
      }

      &.ia-react-core_tabs--large {
        height: 74px;
        padding-top: 25px;
        font-size: 21px;
      }

      &--selected {
        position: relative;
        z-index: 1;
        background: ${({ theme, $background }) => $background || theme.colors.white};
        color: ${({ theme, $selectedColor }) => $selectedColor || theme.colors.primary};
        border-top: ${({ $selectedTabBorderSize }) => $selectedTabBorderSize}px solid ${({ theme, $selectedTabBorder }) => $selectedTabBorder || theme.colors.primary};
        height: 58px;

        &.ia-react-core_tabs--compact {
          height: ${({ $selectedTabBorderSize }: {$selectedTabBorderSize: number}) => 30 + $selectedTabBorderSize}px;
        }

        &.ia-react-core_tabs--small {
          height: ${({ $selectedTabBorderSize }: {$selectedTabBorderSize: number}) => 50 + $selectedTabBorderSize}px;
        }

        &.ia-react-core_tabs--large {
          height: ${({ $selectedTabBorderSize }: {$selectedTabBorderSize: number}) => 74 + $selectedTabBorderSize}px;
        }
      }

      &--disabled {
        background: ${({ theme }) => theme.colors.gray1};
        color: ${({ theme }) => theme?.Input?.disabledColor};
        cursor: default;
      }

      &:focus-visible, &:focus {
        box-shadow: ${({ theme }) => theme.boxShadows?.focus};
      }

      &:focus:not(&:focus-visible) {
        box-shadow: none;
      }

      &:not(&--disabled):not(&--selected):hover {
        color: ${({ theme, $hoverBackground }) => $hoverBackground || theme.colors.white};
        background: rgba(0, 0, 0, 0.65);
        height: 54px;

        &.ia-react-core_tabs--compact {
          height: 30px;
        }

        &.ia-react-core_tabs--small {
          height: 50px;
        }

        &.ia-react-core_tabs--large {
          height: 74px;
        }
      }

      &:not(&--disabled):not(&--selected):active {
        color: ${({ theme, $background }) => $background || theme.colors.white};
        background: rgba(0, 0, 0, 0.7);
        height: 54px;

        &.ia-react-core_tabs--compact {
          height: 30px;
        }

        &.ia-react-core_tabs--small {
          height: 50px;
        }

        &.ia-react-core_tabs--large {
          height: 74px;
        }
      }
    }

    &.ia-react-core_tabs--standard .react-tabs__tab--selected {
      border-left: 0;
      border-right: 0;
      padding-left: 16px;
      padding-right: 16px;

      &.ia-react-core_tabs--compact {
        padding-right: 6px;
        padding-left: 6px;
      }

      ${media.lg`
        padding-left: 32px;
        padding-right: 32px;

        &.ia-react-core_tabs--compact {
          padding-right: 8px;
          padding-left: 8px;
        }
      `}
    }

    &.ia-react-core_tabs--light .react-tabs__tab--selected {
      border-left: ${({ theme }) => `1px solid ${theme.colors.gray4}`};
      border-right: ${({ theme }) => `1px solid ${theme.colors.gray4}`};
      padding-left: 15px;
      padding-right: 15px;

      &.ia-react-core_tabs--compact {
        padding-right: 11px;
        padding-left: 11px;
      }

      ${media.lg`
        padding-left: 31px;
        padding-right: 31px;

        &.ia-react-core_tabs--compact {
          padding-right: 15px;
          padding-left: 15px;
        }
      `}
    }

    &__tab-panel {
      position: relative;
      display: none;
      background: ${({ theme, $background }) => $background || theme.colors.white};
      padding: 36px 16px;
      z-index: 2;

      &.ia-react-core_tabs--compact {
        padding-right: 16px;
        padding-left: 16px;
        padding-top: 16px;
        padding-bottom: 16px;
      }

      ${media.lg`
        padding-left: 32px;
        padding-right: 32px;

        &.ia-react-core_tabs--compact {
          padding-right: 16px;
          padding-left: 16px;
        }
      `}

      ${media.xs<{ paddingXs: string }>`
        ${({ paddingXs }) => paddingXs && css`
          padding: ${paddingXs};
        `}
      `}

      ${media.sm<{ paddingSm: string }>`
        ${({ paddingSm }) => paddingSm && css`
          padding: ${paddingSm};
        `}
      `}

      &--selected {
        display: block;
      }
    }

    &.ia-react-core_tabs--standard .react-tabs__tab-panel {
      border-left: 0;
      border-right: 0;
      border-bottom: 0;
    }

    &.ia-react-core_tabs--light .react-tabs__tab-panel {
      border-left: ${({ theme }) => `1px solid ${theme.colors.gray4}`};
      border-right: ${({ theme }) => `1px solid ${theme.colors.gray4}`};
      border-bottom: ${({ theme }) => `1px solid ${theme.colors.gray4}`};
    }
  }
`;
